import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useDataProvider, useStore, useTranslate } from "ra-core";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import type { NevoraDataProvider } from "../datos/dataProvider";
import { useEsAdmin } from "../datos/useEsAdmin";
import { useResponsables } from "../datos/useResponsables";
import { claveResultadoPorCanal } from "../dominio/catalogos";
import {
  COLUMNAS_RESUMEN,
  conPorcentajeContestadas,
  contestadas,
  moverPeriodo,
  periodoResumen,
  porcentaje,
  totalResumen,
  type FilaResumen,
  type VistaResumen,
} from "../dominio/resumen";
import { useResumenDisponible } from "./useResumenDisponible";

/**
 * «Resumen de llamadas» (28d, Robert 15/10/2026): por día, semana o mes (19/10), por persona y en total,
 * las llamadas («He llamado») con el número y el % de cada resultado. Para todos: Jan y Robert
 * ven a todos (también a ellos mismos); un setter, solo su fila. 40 (Robert, 23/10/2026): más visual,
 * una tarjeta por persona con lo importante (llamadas, contestadas, quieren reunión) y una barra con
 * cómo acaban; la tabla completa, plegada debajo.
 */
export const ResumenPage = () => {
  const translate = useTranslate();
  const esAdmin = useEsAdmin();
  const dataProvider = useDataProvider<NevoraDataProvider>();
  const { nombre } = useResponsables();
  const disponible = useResumenDisponible();
  const [vista, setVista] = useStore<VistaResumen>("resumen.vista", "dia");
  const [ref, setRef] = useState(() => new Date());
  const { desde, hasta } = periodoResumen(vista, ref);
  const { data, isPending, isError } = useQuery({
    queryKey: ["nevora", "resumenLlamadas", desde.toISOString(), vista],
    queryFn: () => dataProvider.resumenLlamadas(desde, hasta),
    enabled: disponible === true,
  });
  const filas = data ?? [];
  const total = totalResumen(filas);
  const conTotal = esAdmin && filas.length > 1;
  // 37: «No se presentó» y «No quiere que le contactemos», solo si la base ya los cuenta.
  const columnas = COLUMNAS_RESUMEN.filter(
    (c) =>
      (c !== "no_se_presento" && c !== "no_quiere_contacto") ||
      filas.some((f) => c in f),
  );

  const etiquetaPeriodo =
    vista === "dia"
      ? desde.toLocaleDateString("es-ES", {
          weekday: "long",
          day: "numeric",
          month: "long",
        })
      : vista === "mes"
        ? desde.toLocaleDateString("es-ES", { month: "long", year: "numeric" })
        : `${desde.toLocaleDateString("es-ES", { day: "numeric", month: "short" })} – ${new Date(
            hasta.getTime() - 1,
          ).toLocaleDateString("es-ES", { day: "numeric", month: "short" })}`;

  const celda = "px-2 py-2 text-right tabular-nums whitespace-nowrap";
  const fila = (
    clave: string,
    persona: string,
    f: Omit<FilaResumen, "sale_id">,
    negrita = false,
  ) => (
    <tr
      key={clave}
      className={cn("border-b last:border-b-0", negrita && "font-semibold")}
      data-testid={`resumen-${clave}`}
    >
      <td className="px-2 py-2 whitespace-nowrap">{persona}</td>
      <td className={cn(celda, "font-medium")} data-testid="resumen-llamadas">
        {f.llamadas}
      </td>
      <td
        className={cn(celda, "font-medium")}
        data-testid="resumen-contestadas"
      >
        {contestadas(f)}
        <Porcentajes total={porcentaje(contestadas(f), Number(f.llamadas))} />
      </td>
      {columnas.map((c) => {
        const n = Number(f[c] ?? 0);
        return (
          <td key={c} className={celda} data-testid={`resumen-${c}`}>
            {n}
            <Porcentajes
              total={porcentaje(n, Number(f.llamadas))}
              contestadas={
                conPorcentajeContestadas(c)
                  ? porcentaje(n, contestadas(f))
                  : undefined
              }
            />
          </td>
        );
      })}
    </tr>
  );

  return (
    <div className="mt-2 space-y-4 pb-8">
      <div className="space-y-1">
        <h1 className="titulo-pagina">{translate("nevora.resumen.titulo")}</h1>
        <p className="text-sm text-muted-foreground">
          {translate(
            esAdmin ? "nevora.resumen.ayuda" : "nevora.resumen.ayuda_setter",
          )}
        </p>
        <p
          className="text-xs text-muted-foreground"
          data-testid="resumen-leyenda"
        >
          {translate("nevora.resumen.leyenda")}
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div
          role="tablist"
          aria-label={translate("nevora.resumen.vistas")}
          className="inline-flex rounded-lg border bg-card p-1 gap-1"
        >
          {(["dia", "semana", "mes"] as const).map((v) => (
            <button
              key={v}
              type="button"
              role="tab"
              aria-selected={vista === v}
              onClick={() => setVista(v)}
              className={cn(
                "px-3 py-1.5 rounded-md text-sm font-medium transition-colors",
                vista === v
                  ? "bg-foreground text-background"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {translate(`nevora.resumen.${v}`)}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="icon"
            aria-label={translate("nevora.resumen.anterior")}
            onClick={() => setRef(moverPeriodo(vista, ref, -1))}
          >
            <ChevronLeft className="size-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setRef(new Date())}
          >
            {translate(
              vista === "dia"
                ? "nevora.resumen.hoy"
                : vista === "mes"
                  ? "nevora.resumen.este_mes"
                  : "nevora.resumen.esta_semana",
            )}
          </Button>
          <Button
            variant="outline"
            size="icon"
            aria-label={translate("nevora.resumen.siguiente")}
            onClick={() => setRef(moverPeriodo(vista, ref, 1))}
          >
            <ChevronRight className="size-4" />
          </Button>
        </div>
        <span
          className="text-sm font-medium first-letter:uppercase"
          data-testid="resumen-periodo"
        >
          {etiquetaPeriodo}
        </span>
      </div>

      {disponible === false ? (
        <p className="text-sm text-muted-foreground">
          {translate("nevora.resumen.no_disponible")}
        </p>
      ) : isError ? (
        <p className="text-sm text-destructive">
          {translate("nevora.resumen.error")}
        </p>
      ) : isPending || disponible === undefined ? (
        <p className="text-sm text-muted-foreground">
          {translate("nevora.resumen.cargando")}
        </p>
      ) : total.llamadas === 0 && !conTotal ? (
        <p
          className="text-sm text-muted-foreground"
          data-testid="resumen-vacio"
        >
          {translate("nevora.resumen.vacio")}
        </p>
      ) : (
        <div className="space-y-4">
          <div
            className="grid gap-3 md:grid-cols-2 xl:grid-cols-3"
            data-testid="resumen-tarjetas"
          >
            {conTotal ? (
              <TarjetaResumen
                titulo={translate("nevora.resumen.equipo")}
                f={total}
                clave="total"
                destacada
              />
            ) : null}
            {filas.map((f) => (
              <TarjetaResumen
                key={String(f.sale_id)}
                titulo={nombre(f.sale_id) || "—"}
                f={f}
                clave={String(f.sale_id)}
              />
            ))}
          </div>
          <details
            className="rounded-lg border bg-card"
            data-testid="resumen-detalle"
          >
            <summary className="cursor-pointer px-4 py-3 text-sm font-medium">
              {translate("nevora.resumen.ver_tabla")}
            </summary>
            <div className="overflow-x-auto border-t">
              <table
                className="w-full text-sm"
                data-testid="resumen-llamadas-tabla"
              >
                <thead>
                  <tr className="border-b text-xs text-muted-foreground">
                    <th className="px-2 py-2 text-left font-medium">
                      {translate("nevora.resumen.persona")}
                    </th>
                    <th className={cn(celda, "font-medium")}>
                      {translate("nevora.resumen.llamadas")}
                    </th>
                    <th className={cn(celda, "font-medium")}>
                      {translate("nevora.resumen.contestadas")}
                    </th>
                    {columnas.map((c) => (
                      <th key={c} className={cn(celda, "font-medium")}>
                        {translate(
                          c === "sin_resultado"
                            ? "nevora.resumen.sin_resultado"
                            : claveResultadoPorCanal("llamada", c),
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filas.map((f) =>
                    fila(String(f.sale_id), nombre(f.sale_id) || "—", f),
                  )}
                  {conTotal
                    ? fila(
                        "total",
                        translate("nevora.resumen.total"),
                        total,
                        true,
                      )
                    : null}
                </tbody>
              </table>
            </div>
          </details>
        </div>
      )}
    </div>
  );
};

ResumenPage.path = "/resumen";

/**
 * Debajo de cada número (Robert, 22/10/2026): el % sobre todas las llamadas y, salvo en «No
 * contesta», el % sobre las contestadas. Cada uno con su nombre, para no confundirlos.
 */
const Porcentajes = ({
  total,
  contestadas,
}: {
  total: number | null;
  contestadas?: number | null;
}) => {
  const translate = useTranslate();
  return (
    <>
      {total != null ? (
        <span className="block text-[11px] text-muted-foreground">
          {translate("nevora.resumen.pct_total", { pct: total })}
        </span>
      ) : null}
      {contestadas != null ? (
        <span
          className="block text-[11px] font-medium text-[#3D8BD4]"
          data-testid="pct-contestadas"
        >
          {translate("nevora.resumen.pct_contestadas", { pct: contestadas })}
        </span>
      ) : null}
    </>
  );
};

/** 40: cómo acaban las llamadas, en este orden y color (lo bueno primero, «No contesta» al final). */
const SEGMENTOS: { clave: (typeof COLUMNAS_RESUMEN)[number]; color: string }[] =
  [
    { clave: "quiere_reunion", color: "bg-emerald-600" },
    { clave: "interesado", color: "bg-[#3D8BD4]" },
    { clave: "ni_si_ni_no", color: "bg-sky-300" },
    { clave: "mas_adelante", color: "bg-amber-400" },
    { clave: "no_interesa", color: "bg-rose-300" },
    { clave: "no_quiere_contacto", color: "bg-rose-600" },
    { clave: "no_se_presento", color: "bg-zinc-500" },
    { clave: "sin_resultado", color: "bg-zinc-300" },
    { clave: "no_contesta", color: "bg-zinc-200 dark:bg-zinc-700" },
  ];

/**
 * 40 (Robert, 23/10/2026): una persona (o todo el equipo) de un vistazo: llamadas, contestadas y
 * cuántas quieren reunión, con su %; debajo, una barra con cómo acaban todas las llamadas.
 */
const TarjetaResumen = ({
  titulo,
  f,
  clave,
  destacada = false,
}: {
  titulo: string;
  f: Omit<FilaResumen, "sale_id">;
  clave: string;
  destacada?: boolean;
}) => {
  const translate = useTranslate();
  const llamadas = Number(f.llamadas) || 0;
  const cont = contestadas(f);
  const reuniones = Number(f.quiere_reunion ?? 0);
  const etiqueta = (c: (typeof COLUMNAS_RESUMEN)[number]) =>
    translate(
      c === "sin_resultado"
        ? "nevora.resumen.sin_resultado"
        : claveResultadoPorCanal("llamada", c),
    );
  const segmentos = SEGMENTOS.map((s) => ({
    ...s,
    n: Number(f[s.clave] ?? 0),
  })).filter((s) => s.n > 0);
  const cifras: { k: string; v: number; pct?: string }[] = [
    { k: "llamadas", v: llamadas },
    {
      k: "contestadas",
      v: cont,
      pct:
        porcentaje(cont, llamadas) != null
          ? translate("nevora.resumen.pct_total", {
              pct: porcentaje(cont, llamadas),
            })
          : undefined,
    },
    {
      k: "quieren_reunion",
      v: reuniones,
      pct:
        porcentaje(reuniones, cont) != null
          ? translate("nevora.resumen.pct_contestadas", {
              pct: porcentaje(reuniones, cont),
            })
          : undefined,
    },
  ];
  return (
    <section
      className={cn(
        "rounded-lg border bg-card p-4 space-y-3",
        destacada && "border-[#3D8BD4]/60",
      )}
      data-testid={`tarjeta-resumen-${clave}`}
    >
      <h2 className="font-medium">{titulo}</h2>
      <dl className="grid grid-cols-3 gap-2">
        {cifras.map((c) => (
          <div key={c.k}>
            <dt className="text-xs text-muted-foreground">
              {translate(`nevora.resumen.${c.k}`)}
            </dt>
            <dd
              className="text-2xl font-semibold tabular-nums"
              data-testid={`tarjeta-${c.k}`}
            >
              {c.v}
            </dd>
            {c.pct ? (
              <dd className="text-[11px] text-muted-foreground">{c.pct}</dd>
            ) : null}
          </div>
        ))}
      </dl>
      {llamadas > 0 ? (
        <>
          <div
            className="flex h-2.5 w-full overflow-hidden rounded-full bg-muted"
            aria-hidden
          >
            {segmentos.map((s) => (
              <div
                key={s.clave}
                className={s.color}
                style={{ width: `${(s.n / llamadas) * 100}%` }}
                title={`${etiqueta(s.clave)}: ${s.n}`}
              />
            ))}
          </div>
          <ul className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
            {segmentos.map((s) => (
              <li key={s.clave} className="flex items-center gap-1">
                <span className={cn("size-2 rounded-full", s.color)} />
                {etiqueta(s.clave)}
                <span className="font-medium tabular-nums text-foreground">
                  {s.n}
                </span>
              </li>
            ))}
          </ul>
        </>
      ) : (
        <p className="text-sm text-muted-foreground">
          {translate("nevora.resumen.sin_llamadas")}
        </p>
      )}
    </section>
  );
};
